<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MultilineNotes',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    ...userComponentProps,
    value: {
      type: null,
      required: true,
    },
    editable: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const beginEdit = (...args) => {
    if (!isEditing.value) {
      isEditing.value = true;
      valueSnapshot.value = textValue.value;
    }
    isMouseOver.value = false;
  };
  const cancelEdit = (...args) => {
    if (isEditing.value) {
      isEditing.value = false;
      textValue.value = valueSnapshot.value;
    }
    isMouseOver.value = false;
  };
  const save = (...args) => {
    try {
      saving.value = true;
      emit('save', {
        value: {
          text: textValue.value,
        },
      });
      isEditing.value = false;
    } finally {
      saving.value = false;
    }
  };

  const isEditing = ref(false);
  const isMouseOver = ref(false);
  const selected = ref(false);
  const clicked = ref(false);
  const textValue = ref(null);
  const saving = ref(false);
  const valueSnapshot = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    isEditing: isEditing.value,
    isMouseOver: isMouseOver.value,
    selected: selected.value,
    clicked: clicked.value,
    textValue: textValue.value,
    saving: saving.value,
    valueSnapshot: valueSnapshot.value,
    ux: ux.value,
  }));

  const $$load_textValue = () => {
    return props.value && props.value.text ? props.value.text : props.value;
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'isEditing' },
      { name: 'isMouseOver' },
      { name: 'selected' },
      { name: 'clicked' },
      { name: 'textValue' },
      { name: 'saving' },
      { name: 'valueSnapshot' },
      { name: 'ux' },
    ],
    loaders: { textValue: $$load_textValue },
    ux: {},
    dataRefs: {
      isEditing,
      isMouseOver,
      selected,
      clicked,
      textValue,
      saving,
      valueSnapshot,
      ux,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.value,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('value');
    }
  );
  watch(
    () => props.editable,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('editable');
    }
  );

  const act_2248226175642056_script_3m7bgece2_value_callable = async (
    $event
  ) => {
    return beginEdit();
  };
  const act_2248226175642056_script_3m7bgece2_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_2248226175642056_script_3m7bgece2_value_callable($event);
      },
    };
  };
  const act_2248226175642056_setData_p7qi09hyx_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'isMouseOver',
      value: true,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2248226175642056_setData_ewvywbds3_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'isMouseOver',
      value: false,
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_1468474219929533_script_n03z59sjo_value_callable = async (
    $event
  ) => {
    return save();
  };
  const act_1468474219929533_script_n03z59sjo_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1468474219929533_script_n03z59sjo_value_callable($event);
      },
    };
  };
  const act_3601807637515735_script_m9sgok6pf_value_callable = async (
    $event
  ) => {
    return cancelEdit();
  };
  const act_3601807637515735_script_m9sgok6pf_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_3601807637515735_script_m9sgok6pf_value_callable($event);
      },
    };
  };

  const act_7315092382398562_reloadComponentData_dxetvf2wt =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_3a8i8eohg =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_dxetvf2wt,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_3a8i8eohg],
    event: 'reload',
  });
  const act_2248226175642056_script_3m7bgece2 = new actionTypes.ScriptAction({
    actionArgs: act_2248226175642056_script_3m7bgece2_getActionArgs,
  });
  const inst_evh_2248226175642056_dblclick_thf9fydqs =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_script_3m7bgece2,
      event: 'dblclick',
      displayName: 'script',
    });
  const inst_evh_2248226175642056_dblclick = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2248226175642056_dblclick_thf9fydqs],
    event: 'dblclick',
  });
  const act_2248226175642056_setData_p7qi09hyx = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_p7qi09hyx_getActionArgs,
  });
  const inst_evh_2248226175642056_mouseover_4357nh5wl =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_p7qi09hyx,
      event: 'mouseover',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_mouseover = new actionTypes.EventHandlerGroup(
    {
      handlers: [inst_evh_2248226175642056_mouseover_4357nh5wl],
      event: 'mouseover',
    }
  );
  const act_2248226175642056_setData_ewvywbds3 = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_ewvywbds3_getActionArgs,
  });
  const inst_evh_2248226175642056_mouseleave_acnhhwy2f =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_ewvywbds3,
      event: 'mouseleave',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_mouseleave =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_mouseleave_acnhhwy2f],
      event: 'mouseleave',
    });
  const act_1468474219929533_script_n03z59sjo = new actionTypes.ScriptAction({
    actionArgs: act_1468474219929533_script_n03z59sjo_getActionArgs,
  });
  const inst_evh_1468474219929533_click_l8qk6hcnp =
    new actionTypes.EventHandlerImpl({
      action: act_1468474219929533_script_n03z59sjo,
      event: 'click',
      displayName: 'script',
    });
  const inst_evh_1468474219929533_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1468474219929533_click_l8qk6hcnp],
    event: 'click',
  });
  const act_3601807637515735_script_m9sgok6pf = new actionTypes.ScriptAction({
    actionArgs: act_3601807637515735_script_m9sgok6pf_getActionArgs,
  });
  const inst_evh_3601807637515735_click_bxr3ggcuc =
    new actionTypes.EventHandlerImpl({
      action: act_3601807637515735_script_m9sgok6pf,
      event: 'click',
      displayName: 'script',
    });
  const inst_evh_3601807637515735_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_3601807637515735_click_bxr3ggcuc],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_reload_3a8i8eohg,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_dblclick_thf9fydqs,
    inst_evh_2248226175642056_dblclick,
    inst_evh_2248226175642056_mouseover_4357nh5wl,
    inst_evh_2248226175642056_mouseover,
    inst_evh_2248226175642056_mouseleave_acnhhwy2f,
    inst_evh_2248226175642056_mouseleave,
    inst_evh_1468474219929533_click_l8qk6hcnp,
    inst_evh_1468474219929533_click,
    inst_evh_3601807637515735_click_bxr3ggcuc,
    inst_evh_3601807637515735_click,
  };

  const evh_7315092382398562_reload_3a8i8eohg = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_3a8i8eohg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_dblclick_thf9fydqs = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_dblclick_thf9fydqs.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_dblclick = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_dblclick.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_mouseover_4357nh5wl = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_mouseover_4357nh5wl.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_mouseover = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_mouseover.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_mouseleave_acnhhwy2f = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_mouseleave_acnhhwy2f.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_mouseleave = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_mouseleave.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1468474219929533_click_l8qk6hcnp = async (event, scope) => {
    return await actions.inst_evh_1468474219929533_click_l8qk6hcnp.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1468474219929533_click = async (event, scope) => {
    return await actions.inst_evh_1468474219929533_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3601807637515735_click_bxr3ggcuc = async (event, scope) => {
    return await actions.inst_evh_3601807637515735_click_bxr3ggcuc.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_3601807637515735_click = async (event, scope) => {
    return await actions.inst_evh_3601807637515735_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div
      :class="{ 'bg-gray-200': isMouseOver }"
      v-if="!isEditing"
      @dblclick="evh_2248226175642056_dblclick($event, {})"
      @mouseover="evh_2248226175642056_mouseover($event, {})"
      @mouseleave="evh_2248226175642056_mouseleave($event, {})"
    >
      <ItpText :text="textValue" pre> </ItpText>
    </div>
    <div v-if="isEditing">
      <ItpFormTextarea name="notes" v-model="textValue" pre> </ItpFormTextarea>
      <ItpBox alignItems="end" class="mt-1 mb-2">
        <ItpButton
          :text="gettext('Save')"
          variant="primary"
          :spinning="saving"
          size="sm"
          class="ms-auto me-1"
          @click="evh_1468474219929533_click($event, {})"
        >
        </ItpButton>
        <ItpButton
          :text="gettext('Cancel')"
          size="sm"
          @click="evh_3601807637515735_click($event, {})"
        >
        </ItpButton>
      </ItpBox>
    </div>
  </ItpUsercontrol>
</template>
